import type { PatientDemographics } from '@/api/patientsApi'

/** The name-bearing subset of a patient record — accepts list items and details alike. */
type NameFields = Pick<PatientDemographics, 'fullName' | 'firstName' | 'lastName'>

/**
 * A patient's display name, or null when none is known.
 *
 * Null rather than a fallback string so callers decide how absence reads: the list
 * shows an em dash, the banner falls back to the MRN. Mirrors the backend's
 * `fullName` derivation, so an integration-created patient and a manually created
 * one display identically.
 */
export function patientName(p: NameFields): string | null {
  const full = p.fullName?.trim()
  if (full) return full
  const parts = [p.firstName, p.lastName].map(s => s?.trim()).filter(Boolean)
  return parts.length > 0 ? parts.join(' ') : null
}

/**
 * First and last name as separate values.
 *
 * Prefers the discrete columns. Falls back to splitting `fullName` for a patient
 * whose record only carried a combined name (the EHR sends `name.text` without
 * `given`/`family`), so the name columns aren't blank when a name plainly exists.
 */
export function patientNameParts(p: NameFields): { first: string | null; last: string | null } {
  const first = p.firstName?.trim() || null
  const last = p.lastName?.trim() || null
  if (first || last) return { first, last }

  const full = p.fullName?.trim()
  if (!full) return { first: null, last: null }
  const [head, ...rest] = full.split(/\s+/)
  return { first: head, last: rest.length > 0 ? rest.join(' ') : null }
}

/** Up to two initials for the avatar. Falls back to the MRN when there is no name. */
export function patientInitials(name: string | null, mrn: string): string {
  const source = name?.trim()
  if (!source) return mrn.replace(/^MRN-/i, '').slice(0, 2).toUpperCase()
  return source
    .split(/\s+/)
    .filter(Boolean)
    .map(w => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

/**
 * A calendar date — a date of birth — as a LOCAL-midnight Date of the day it
 * names (z8nrz7dp77).
 *
 * Takes the `YYYY-MM-DD` the value starts with and ignores anything after it.
 * The API sends a date of birth as the bare `YYYY-MM-DD`; an older one sent the
 * UTC instant `2000-01-01T00:00:00.000Z`, and `new Date(that)` rendered Dec 31,
 * 1999 for anyone west of Greenwich. The day written is the day meant, so no
 * timezone is ever applied. Returns null for a value that is not a real day.
 */
export function parseCalendarDate(iso: string | null | undefined): Date | null {
  if (!iso) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return null
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])]
  const date = new Date(y, mo - 1, d)
  return date.getFullYear() === y && date.getMonth() === mo - 1 && date.getDate() === d ? date : null
}

/** A calendar date in the viewer's short numeric format, e.g. `1/1/2000`, or '' for none. */
export function formatCalendarDateShort(iso: string | null | undefined): string {
  return parseCalendarDate(iso)?.toLocaleDateString() ?? ''
}

/** Today as `YYYY-MM-DD` in the viewer's own calendar — the latest valid date of birth. */
export function todayCalendarDate(now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

const parseIsoDate = parseCalendarDate

/** Format an ISO date for display, e.g. `Mar 17, 1967`. */
export function formatDate(iso: string | null | undefined): string | null {
  const d = parseIsoDate(iso)
  if (!d) return null
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

/**
 * Age as `59 years, 4 months`, computed from birthDate rather than the whole-year
 * `age` the API derives — clinically, months matter, and a bare year is a blunt
 * instrument for paediatric patients especially.
 *
 * Under one year the years part is dropped entirely (`7 months`), and a newborn
 * reads `0 months` rather than an empty string.
 */
export function formatAge(birthDate: string | null | undefined, now = new Date()): string | null {
  const d = parseIsoDate(birthDate)
  if (!d) return null

  let years = now.getFullYear() - d.getFullYear()
  let months = now.getMonth() - d.getMonth()
  // The current month only counts once the day-of-month has been reached.
  if (now.getDate() < d.getDate()) months -= 1
  if (months < 0) {
    years -= 1
    months += 12
  }
  if (years < 0) return null

  const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? '' : 's'}`
  const monthPart = plural(months, 'month')
  return years === 0 ? monthPart : `${plural(years, 'year')}, ${monthPart}`
}

/** `male` → `Male`. The backend lowercases on ingest; presentation re-capitalises. */
export function formatSex(sex: string | null | undefined): string | null {
  const s = sex?.trim()
  if (!s) return null
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/**
 * A patient lookup that failed only because the MRN has no Patient row. That
 * is not an error: procedures and forms can exist for an MRN with none, and
 * the patient pages open for it, so they show it as unrecorded, not failed.
 */
export function isPatientNotFound(error: unknown): boolean {
  return error instanceof Error && /not found/i.test(error.message)
}
